import React, { useState, useEffect, useContext } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { 
  getBlogPosts, 
  getFeaturedPosts, 
  getRecentPosts, 
  getPopularPosts,
  getBlogCategories,
  getBlogTags,
  likeBlogPost,
  bookmarkBlogPost,
  subscribeToNewsletter
} from '../api/api';
import './BlogPage.css';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    tag: searchParams.get('tag') || '',
    search: searchParams.get('search') || ''
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch initial data
  useEffect(() => {
    fetchBlogData();
    fetchSidebarData();
  }, [filters]);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === '') {
          delete params[key];
        }
      });
      
      const data = await getBlogPosts(params);
      
      if (data.results) {
        // DRF pagination format
        setPosts(data.results);
        setPagination({
          count: data.count,
          next: data.next,
          previous: data.previous,
          page: pagination.page
        });
      } else {
        // Non-paginated response
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSidebarData = async () => {
    try {
      const [
        featured, 
        recent, 
        popular, 
        cats, 
        tagList
      ] = await Promise.all([
        getFeaturedPosts(),
        getRecentPosts(5),
        getPopularPosts(5),
        getBlogCategories(),
        getBlogTags()
      ]);
      
      setFeaturedPosts(featured);
      setRecentPosts(recent);
      setPopularPosts(popular);
      setCategories(cats);
      setTags(tagList);
    } catch (error) {
      console.error('Error fetching sidebar data:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k]) {
        params.set(k, newFilters[k]);
      }
    });
    setSearchParams(params);
  };

  const handleLike = async (postId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/blog' } });
      return;
    }
    
    try {
      const result = await likeBlogPost(postId);
      
      // Update local state
      setPosts(posts.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              user_liked: result.liked,
              likes_count: result.likes_count 
            } 
          : post
      ));
      
      // Update featured posts
      setFeaturedPosts(featuredPosts.map(post =>
        post.id === postId
          ? {
              ...post,
              user_liked: result.liked,
              likes_count: result.likes_count
            }
          : post
      ));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleBookmark = async (postId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/blog' } });
      return;
    }
    
    try {
      const result = await bookmarkBlogPost(postId);
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, user_bookmarked: result.bookmarked } 
          : post
      ));
    } catch (error) {
      console.error('Error bookmarking post:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail) {
      setNewsletterMessage('Please enter your email address');
      return;
    }
    
    try {
      await subscribeToNewsletter(newsletterEmail);
      setNewsletterMessage('Thank you for subscribing to our newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      setNewsletterMessage('Subscription failed. Please try again.');
    }
  };

  const loadMore = async () => {
    if (!pagination.next || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      const nextPage = pagination.page + 1;
      const params = {
        page: nextPage,
        ...filters
      };
      
      const data = await getBlogPosts(params);
      
      if (data.results) {
        setPosts([...posts, ...data.results]);
        setPagination({
          count: data.count,
          next: data.next,
          previous: data.previous,
          page: nextPage
        });
      }
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (minutes) => {
    return `${minutes} min read`;
  };

  const getExcerpt = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderPostCard = (post) => (
    <div key={post.id} className="blog-post-card">
      {post.featured_image && (
        <div className="post-image">
          <img 
            src={post.featured_image} 
            alt={post.title} 
            onClick={() => navigate(`/blog/${post.id}`)}
          />
        </div>
      )}
      
      <div className="post-content">
        <div className="post-meta">
          {post.category && (
            <Link 
              to={`/blog?category=${post.category.slug}`}
              className="category-badge"
            >
              {post.category.name}
            </Link>
          )}
          <span className="post-date">{formatDate(post.published_at)}</span>
          <span className="reading-time">{getReadingTime(post.reading_time)}</span>
        </div>
        
        <h3 className="post-title">
          <Link to={`/blog/${post.id}`}>{post.title}</Link>
        </h3>
        
        <p className="post-excerpt">{getExcerpt(post.excerpt || post.content)}</p>
        
        <div className="post-footer">
          <div className="author-info">
            {post.author && (
              <span className="author-name">{post.author.username}</span>
            )}
          </div>
          
          <div className="post-stats">
            <button 
              className={`like-btn ${post.user_liked ? 'liked' : ''}`}
              onClick={() => handleLike(post.id)}
            >
              <span className="icon">❤️</span>
              <span>{post.likes_count || 0}</span>
            </button>
            
            <button 
              className={`bookmark-btn ${post.user_bookmarked ? 'bookmarked' : ''}`}
              onClick={() => handleBookmark(post.id)}
            >
              <span className="icon">🔖</span>
            </button>
            
            <span className="views">
              👁️ {post.views || 0} views
            </span>
          </div>
        </div>
        
        {post.tags && post.tags.length > 0 && (
          <div className="post-tags">
            {post.tags.map(tag => (
              <Link 
                key={tag.id} 
                to={`/blog?tag=${tag.slug}`}
                className="tag"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="blog-container">
        <div className="loading-spinner">Loading blog posts...</div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      {/* Hero Section */}
      <section className="blog-hero">
        <h1 className="blog-title">Our Blog</h1>
        <p className="blog-subtitle">Insights, tips, and updates from our team</p>
        
        <div className="search-box">
          <input
            type="text"
            placeholder="Search blog posts..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
          <button className="search-btn">🔍</button>
        </div>
      </section>

      <div className="blog-layout">
        {/* Main Content */}
        <main className="blog-main">
          {/* Featured Posts */}
          {featuredPosts.length > 0 && (
            <section className="featured-posts">
              <h2 className="section-title">Featured Posts</h2>
              <div className="featured-grid">
                {featuredPosts.map(post => renderPostCard(post))}
              </div>
            </section>
          )}

          {/* All Posts */}
          <section className="all-posts">
            <div className="section-header">
              <h2 className="section-title">Latest Articles</h2>
              
              <div className="filter-controls">
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name} ({cat.post_count || 0})
                    </option>
                  ))}
                </select>
                
                <select
                  value={filters.tag}
                  onChange={(e) => handleFilterChange('tag', e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Tags</option>
                  {tags.map(tag => (
                    <option key={tag.id} value={tag.slug}>
                      {tag.name} ({tag.post_count || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {posts.length === 0 ? (
              <div className="no-posts">
                <p>No posts found. Try different filters or check back later!</p>
              </div>
            ) : (
              <>
                <div className="posts-grid">
                  {posts.map(post => renderPostCard(post))}
                </div>
                
                {pagination.next && (
                  <div className="load-more-container">
                    <button 
                      onClick={loadMore} 
                      disabled={loadingMore}
                      className="load-more-btn"
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </main>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          {/* Categories */}
          <div className="sidebar-widget">
            <h3 className="widget-title">Categories</h3>
            <ul className="category-list">
              {categories.map(category => (
                <li key={category.id}>
                  <Link 
                    to={`/blog?category=${category.slug}`}
                    className={filters.category === category.slug ? 'active' : ''}
                  >
                    {category.name}
                    <span className="count">({category.post_count || 0})</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Recent Posts */}
          <div className="sidebar-widget">
            <h3 className="widget-title">Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentPosts.map(post => (
                <li key={post.id}>
                  <Link to={`/blog/${post.id}`} className="recent-post">
                    <span className="recent-post-title">{post.title}</span>
                    <span className="recent-post-date">{formatDate(post.published_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Posts */}
          <div className="sidebar-widget">
            <h3 className="widget-title">Popular Posts</h3>
            <ul className="popular-posts-list">
              {popularPosts.map((post, index) => (
                <li key={post.id}>
                  <Link to={`/blog/${post.id}`} className="popular-post">
                    <span className="popular-post-rank">{index + 1}</span>
                    <span className="popular-post-title">{post.title}</span>
                    <span className="popular-post-views">👁️ {post.views || 0}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="sidebar-widget newsletter-widget">
            <h3 className="widget-title">Subscribe to Newsletter</h3>
            <p className="newsletter-desc">
              Get the latest posts delivered right to your inbox.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <input
                type="email"
                placeholder="Your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="newsletter-input"
                required
              />
              <button type="submit" className="newsletter-btn">
                Subscribe
              </button>
            </form>
            
            {newsletterMessage && (
              <p className="newsletter-message">{newsletterMessage}</p>
            )}
          </div>

          {/* Tags */}
          <div className="sidebar-widget">
            <h3 className="widget-title">Popular Tags</h3>
            <div className="tags-cloud">
              {tags.slice(0, 15).map(tag => (
                <Link 
                  key={tag.id} 
                  to={`/blog?tag=${tag.slug}`}
                  className="tag-cloud"
                >
                  #{tag.name}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogPage;